import { useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  endpoints,
  type CampaignChannelResponse,
  type DeliverableItem,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { fmtDate, fmtNumber, fmtTimestamp, platformLabel } from "@/lib/format";

// CampaignReport — v2.7.0 MVP. A print-friendly, share-with-boss roll-up
// of one campaign. Reuses /api/campaigns/:id + /:id/deliverables — no new
// backend. Two exports:
//   🖨 导出 PDF       → window.print() (browser handles save as PDF)
//   📋 复制 Markdown → builds a tidy Markdown string + clipboard
//
// Layout chunks (top→bottom): header → overview metrics → per-KOL table
// → per-video cards → notes. All chrome (nav, buttons) hides under
// @media print so the printed page is the report and nothing else.

const STATUS_LABEL: Record<string, string> = {
  planned: "计划中",
  active: "进行中",
  completed: "已完成",
  cancelled: "已取消",
  archived: "已归档",
};

export default function CampaignReport() {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const id = Number(params.id);
  const [copied, setCopied] = useState(false);

  const campaignQ = useQuery({
    queryKey: ["campaign", id],
    queryFn: () => endpoints.campaign(id),
    enabled: Number.isFinite(id),
  });
  const deliverablesQ = useQuery({
    queryKey: ["campaign-deliverables", id],
    queryFn: () => endpoints.campaignDeliverables(id),
    enabled: Number.isFinite(id),
  });

  // v2.7.1 — LLM takeaway. Operator-driven; we stash the latest result
  // in the campaign cache so the report shows it immediately + survives
  // a refresh until the operator regenerates.
  const takeawayMut = useMutation({
    mutationFn: () => endpoints.generateCampaignTakeaway(id),
    onSuccess: (data) => {
      qc.setQueryData(["campaign", id], (prev: typeof campaignQ.data) =>
        prev
          ? {
              ...prev,
              llm_takeaway: data.llm_takeaway,
              llm_takeaway_generated_at: data.llm_takeaway_generated_at,
            }
          : prev,
      );
    },
  });

  const campaign = campaignQ.data;
  const channels = campaign?.channels ?? [];
  const deliverables = deliverablesQ.data ?? [];

  // Confirmed-only counts in the rollup — pending/rejected don't reflect
  // real outcomes yet (matches CampaignPerformanceSummary's policy).
  const confirmed = useMemo(
    () => deliverables.filter((d) => d.review_status === "confirmed"),
    [deliverables],
  );

  const totals = useMemo(() => {
    const views = confirmed.reduce((a, d) => a + (d.view_count ?? 0), 0);
    const likes = confirmed.reduce((a, d) => a + (d.like_count ?? 0), 0);
    const comments = confirmed.reduce((a, d) => a + (d.comment_count ?? 0), 0);
    const engagement = views > 0 ? ((likes + comments) / views) * 100 : null;
    const spendByCur = campaign?.total_spend_by_currency ?? {};
    const currencies = Object.keys(spendByCur);
    const singleCur = currencies.length === 1 ? currencies[0] : null;
    const totalSpend = singleCur ? spendByCur[singleCur] : null;
    const cpm =
      totalSpend !== null && views > 0 ? (totalSpend * 1000) / views : null;
    return {
      views,
      likes,
      comments,
      engagement,
      spendByCur,
      currencies,
      singleCur,
      totalSpend,
      cpm,
    };
  }, [confirmed, campaign?.total_spend_by_currency]);

  // Per-channel rollup for the KOL table.
  const perChannel = useMemo(() => {
    const map = new Map<number, {
      channel: CampaignChannelResponse;
      videos: DeliverableItem[];
      views: number;
      likes: number;
      comments: number;
    }>();
    for (const ch of channels) {
      map.set(ch.channel_id, {
        channel: ch,
        videos: [],
        views: 0,
        likes: 0,
        comments: 0,
      });
    }
    for (const d of confirmed) {
      const e = map.get(d.video_channel_id);
      if (!e) continue;
      e.videos.push(d);
      e.views += d.view_count ?? 0;
      e.likes += d.like_count ?? 0;
      e.comments += d.comment_count ?? 0;
    }
    return Array.from(map.values()).sort((a, b) => b.views - a.views);
  }, [channels, confirmed]);

  // v2.7.1 — Chart datasets. Timeline = each delivered video as a point on a
  // date × views grid; CPM = horizontal bars per-KOL, undelivered = 0 + gray.
  const timelineData = useMemo(
    () =>
      confirmed
        .filter((d) => d.published_at)
        .map((d) => {
          const ts = new Date(d.published_at!).getTime();
          const ch = channels.find((c) => c.channel_id === d.video_channel_id);
          return {
            ts,
            date: d.published_at!.slice(0, 10),
            views: d.view_count ?? 0,
            kol: ch?.channel_name ?? `#${d.video_channel_id}`,
            title: d.title ?? "",
          };
        })
        .sort((a, b) => a.ts - b.ts),
    [confirmed, channels],
  );

  const cpmChartData = useMemo(() => {
    return perChannel
      .map((e) => {
        const ch = e.channel;
        const cpm =
          ch.deal_amount != null && e.views > 0
            ? (ch.deal_amount * 1000) / e.views
            : null;
        return {
          kol: ch.channel_name ?? `#${ch.channel_id}`,
          cpm,
          delivered: e.videos.length > 0,
          dealAmount: ch.deal_amount ?? null,
          views: e.views,
        };
      })
      .sort((a, b) => {
        // delivered + has-cpm rows ascending by CPM (cheapest at top),
        // undelivered rows pushed to the bottom.
        if (a.cpm == null && b.cpm == null) return 0;
        if (a.cpm == null) return 1;
        if (b.cpm == null) return -1;
        return a.cpm - b.cpm;
      });
  }, [perChannel]);

  if (!Number.isFinite(id)) {
    return <div className="p-6 text-sm text-destructive">无效的投放 ID</div>;
  }
  if (campaignQ.isLoading || !campaign) {
    return <div className="p-6 text-sm text-muted-foreground">加载中…</div>;
  }

  const period = (() => {
    const s = campaign.start_date ? fmtDate(campaign.start_date) : "—";
    const e = campaign.end_date ? fmtDate(campaign.end_date) : "至今";
    return `${s} ~ ${e}`;
  })();

  const generatedAt = new Date().toISOString().slice(0, 10);

  // ─── Markdown builder (copies same data) ────────────────────────────
  const buildMarkdown = () => {
    const lines: string[] = [];
    lines.push(`# 投放复盘报告 · ${campaign.name}`);
    lines.push("");
    lines.push(
      `**时段**: ${period}　**状态**: ${STATUS_LABEL[campaign.status] ?? campaign.status}　**生成日期**: ${generatedAt}`,
    );
    lines.push("");
    lines.push("## 概览");
    lines.push("");
    lines.push("| 指标 | 数值 |");
    lines.push("|---|---|");
    lines.push(`| KOL 数 | ${channels.length} |`);
    lines.push(
      `| 投放金额 | ${totals.currencies.length ? Object.entries(totals.spendByCur).map(([c, v]) => `${fmtNumber(v)} ${c}`).join(" + ") : "—"} |`,
    );
    lines.push(`| 交付视频数(已确认) | ${confirmed.length} |`);
    lines.push(`| 总曝光 | ${fmtNumber(totals.views)} |`);
    lines.push(`| 总点赞 | ${fmtNumber(totals.likes)} |`);
    lines.push(`| 总评论 | ${fmtNumber(totals.comments)} |`);
    lines.push(
      `| 互动率 | ${totals.engagement !== null ? totals.engagement.toFixed(2) + "%" : "—"} |`,
    );
    lines.push(
      `| 平均 CPM | ${totals.cpm !== null && totals.singleCur ? `${totals.cpm.toFixed(2)} ${totals.singleCur}` : "—"} |`,
    );
    lines.push("");
    lines.push("## KOL 表现");
    lines.push("");
    lines.push("| KOL | 平台 | 粉丝 | 合同 | 已交付 | 曝光 | 互动 | CPM |");
    lines.push("|---|---|---|---|---|---|---|---|");
    for (const e of perChannel) {
      const ch = e.channel;
      const deal = ch.deal_amount != null
        ? `${fmtNumber(ch.deal_amount)} ${ch.payment_currency ?? ""}`
        : (ch.has_deal_amount ? "已加密" : "—");
      const engPct = e.views > 0 ? (((e.likes + e.comments) / e.views) * 100).toFixed(2) + "%" : "—";
      const cpmStr = (ch.deal_amount != null && e.views > 0)
        ? ((ch.deal_amount * 1000) / e.views).toFixed(2) + " " + (ch.payment_currency ?? "")
        : "—";
      lines.push(
        `| ${ch.channel_name ?? `#${ch.channel_id}`} | ${platformLabel(ch.channel_platform ?? "")} | ${fmtNumber(ch.channel_follower_count ?? 0)} | ${deal} | ${e.videos.length} | ${fmtNumber(e.views)} | ${engPct} | ${cpmStr} |`,
      );
    }
    lines.push("");
    if (confirmed.length > 0) {
      lines.push("## 已交付内容");
      lines.push("");
      for (const d of confirmed) {
        const kol = channels.find((c) => c.channel_id === d.video_channel_id);
        lines.push(`### ${d.title ?? `Video #${d.video_id}`}`);
        lines.push(
          `- **频道**: ${kol?.channel_name ?? "—"}　**发布**: ${d.published_at ? fmtDate(d.published_at) : "—"}　**链接**: ${d.url ?? "—"}`,
        );
        lines.push(
          `- **数据**: 曝光 ${fmtNumber(d.view_count ?? 0)} · 点赞 ${fmtNumber(d.like_count ?? 0)} · 评论 ${fmtNumber(d.comment_count ?? 0)}`,
        );
        lines.push("");
      }
    }
    if (campaign.notes) {
      lines.push("## 备注");
      lines.push("");
      lines.push(campaign.notes);
      lines.push("");
    }
    return lines.join("\n");
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(buildMarkdown());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      alert("复制失败:" + (e as Error).message);
    }
  };

  return (
    <>
      {/* Inline print CSS — survives bundling, keeps the page self-contained.
          Layout `<header>` is hidden via tailwind `print:hidden`; rest of
          the app chrome is outside this route. */}
      <style>{`
        @page { size: A4 portrait; margin: 1.4cm; }
        @media print {
          .report-toolbar { display: none !important; }
          body { background: white !important; }
          .report-page { padding: 0 !important; max-width: 100% !important; font-size: 11pt; line-height: 1.55; }
          /* Cards become flat — drop shadows print as gray boxes. */
          .report-card { box-shadow: none !important; border: 1px solid #ddd !important; break-inside: avoid; padding: 1rem !important; }
          .report-video-card { break-inside: avoid; padding: 0.9rem !important; }
          .report-overview { break-inside: avoid; }
          /* Force "已交付内容" + "备注" sections onto their own pages so each
             gets generous space — without this Chrome happily compresses a
             10-row table + 6 cards into one A4 page and the report stops
             feeling like a report. */
          .report-section-break { break-before: page; page-break-before: always; }
          /* Add a little breathing room between video cards. */
          .report-video-card + .report-video-card { margin-top: 0.6rem; }
          a { color: #333 !important; text-decoration: underline; }
          /* Force background colors to render (Chrome / Edge default-strip them). */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          /* Bigger section titles on paper. */
          h1 { font-size: 22pt !important; }
          h2 { font-size: 14pt !important; margin-top: 0.4rem !important; }
        }
      `}</style>

      <div className="h-full overflow-y-auto bg-muted/30 print:h-auto print:overflow-visible">
        {/* Toolbar — hidden in print */}
        <div className="report-toolbar sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-background px-6 py-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/campaigns/${id}`)}>
            ← 返回活动详情
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={copyMarkdown}>
              📋 {copied ? "已复制" : "复制 Markdown"}
            </Button>
            <Button variant="default" size="sm" onClick={() => window.print()}>
              🖨 导出 PDF
            </Button>
          </div>
        </div>
        {/* Print hint — only visible on screen, helps operator pick the right
            print settings the first time (otherwise browser default may
            shrink to one page and clip the video cards). */}
        <div className="report-toolbar border-b border-border bg-amber-50/60 px-6 py-1.5 text-[11px] text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          打印时建议:<b>勾选"Background graphics"</b>(底色才会渲染),其余默认即可。"已交付内容"段会自动跳到第二页。
        </div>

        <div className="report-page mx-auto max-w-4xl space-y-6 px-8 py-8 text-foreground">
          {/* ── Header ─────────────────────────────────────────── */}
          <header className="border-b border-border pb-4">
            <h1 className="text-2xl font-bold">投放复盘报告</h1>
            <div className="mt-1 text-xl">{campaign.name}</div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span>{period}</span>
              <span>·</span>
              <Badge variant="secondary">{STATUS_LABEL[campaign.status] ?? campaign.status}</Badge>
              <span>·</span>
              <span>生成于 {generatedAt}</span>
            </div>
          </header>

          {/* ── Overview metrics ─────────────────────────────── */}
          <section className="report-overview report-card grid grid-cols-2 gap-4 rounded border border-border bg-background p-5 sm:grid-cols-4">
            <Metric label="KOL 数" value={String(channels.length)} />
            <Metric
              label="投放金额"
              value={
                totals.currencies.length
                  ? Object.entries(totals.spendByCur)
                      .map(([c, v]) => `${fmtNumber(v)} ${c}`)
                      .join(" + ")
                  : "—"
              }
            />
            <Metric label="交付视频" value={String(confirmed.length)} />
            <Metric label="总曝光" value={fmtNumber(totals.views)} />
            <Metric label="总点赞" value={fmtNumber(totals.likes)} />
            <Metric label="总评论" value={fmtNumber(totals.comments)} />
            <Metric
              label="互动率"
              value={totals.engagement !== null ? totals.engagement.toFixed(2) + "%" : "—"}
            />
            <Metric
              label="平均 CPM"
              value={
                totals.cpm !== null && totals.singleCur
                  ? `${totals.cpm.toFixed(2)} ${totals.singleCur}`
                  : "—"
              }
            />
          </section>

          {/* ── LLM Takeaway card (v2.7.1) ─────────────────── */}
          <section className="report-card rounded border border-primary/30 bg-primary/5 p-5">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-lg font-semibold">📊 复盘解读</h2>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {campaign.llm_takeaway_generated_at && (
                  <span>生成于 {fmtTimestamp(campaign.llm_takeaway_generated_at)}</span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => takeawayMut.mutate()}
                  disabled={takeawayMut.isPending}
                  className="report-toolbar"
                  title={
                    campaign.llm_takeaway
                      ? "重新生成解读 (会重新调一次 LLM)"
                      : "用 LLM 根据本活动数据生成 2-3 句中文 takeaway"
                  }
                >
                  {takeawayMut.isPending
                    ? "生成中…"
                    : campaign.llm_takeaway
                      ? "↻ 重新生成"
                      : "✨ 生成解读"}
                </Button>
              </div>
            </div>
            {takeawayMut.isError && (
              <div className="mt-2 text-xs text-destructive">
                生成失败:{(takeawayMut.error as Error).message}
              </div>
            )}
            {campaign.llm_takeaway ? (
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {campaign.llm_takeaway}
              </p>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                还未生成。点右上"✨ 生成解读"用 LLM 根据本活动数据自动写一段
                2-3 句中文 takeaway。生成后会缓存,可随时重新生成。
              </p>
            )}
          </section>

          {/* ── Per-KOL table ─────────────────────────────────── */}
          <section className="report-card rounded border border-border bg-background p-5">
            <h2 className="mb-3 text-lg font-semibold">KOL 表现</h2>
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-2">KOL</th>
                  <th className="py-2 pr-2">平台</th>
                  <th className="py-2 pr-2 text-right">粉丝</th>
                  <th className="py-2 pr-2 text-right">合同</th>
                  <th className="py-2 pr-2 text-right">已交付</th>
                  <th className="py-2 pr-2 text-right">总曝光</th>
                  <th className="py-2 pr-2 text-right">互动率</th>
                  <th className="py-2 text-right">CPM</th>
                </tr>
              </thead>
              <tbody>
                {perChannel.map((e) => {
                  const ch = e.channel;
                  const engPct =
                    e.views > 0
                      ? (((e.likes + e.comments) / e.views) * 100).toFixed(2) + "%"
                      : "—";
                  const cpmStr =
                    ch.deal_amount != null && e.views > 0
                      ? ((ch.deal_amount * 1000) / e.views).toFixed(2) +
                        " " +
                        (ch.payment_currency ?? "")
                      : "—";
                  return (
                    <tr key={ch.id} className="border-b border-border/40">
                      <td className="py-2 pr-2">
                        <Link
                          to={`/channels/${ch.channel_id}`}
                          className="hover:underline"
                        >
                          {ch.channel_name ?? `#${ch.channel_id}`}
                        </Link>
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground">
                        {platformLabel(ch.channel_platform ?? "")}
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums">
                        {fmtNumber(ch.channel_follower_count ?? 0)}
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums">
                        {ch.deal_amount != null
                          ? `${fmtNumber(ch.deal_amount)} ${ch.payment_currency ?? ""}`
                          : ch.has_deal_amount
                            ? "已加密"
                            : "—"}
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums">
                        {e.videos.length}
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums">
                        {fmtNumber(e.views)}
                      </td>
                      <td className="py-2 pr-2 text-right tabular-nums">{engPct}</td>
                      <td className="py-2 text-right tabular-nums">{cpmStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>

          {/* ── Charts (v2.7.1) ─────────────────────────────────── */}
          {confirmed.length > 0 && (
            <section className="report-card grid grid-cols-1 gap-4 rounded border border-border bg-background p-5 lg:grid-cols-2">
              <div>
                <h3 className="mb-3 text-sm font-semibold">曝光 timeline</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        type="number"
                        dataKey="ts"
                        domain={["dataMin", "dataMax"]}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(ts: number) =>
                          new Date(ts).toISOString().slice(5, 10)
                        }
                      />
                      <YAxis
                        type="number"
                        dataKey="views"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) => fmtNumber(v)}
                      />
                      <Tooltip
                        cursor={{ stroke: "hsl(var(--border))" }}
                        contentStyle={{
                          background: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          fontSize: 11,
                        }}
                        labelFormatter={(_label, payload) => {
                          const p = payload?.[0]?.payload;
                          return p ? `${p.date} · ${p.kol}` : "";
                        }}
                        formatter={(value) => [fmtNumber(Number(value)), "曝光"]}
                      />
                      <Scatter data={timelineData} fill="hsl(var(--primary))" />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  每个点 = 一条已交付视频(X 轴发布日期,Y 轴曝光)
                </p>
              </div>

              <div>
                <h3 className="mb-3 text-sm font-semibold">KOL CPM 排名</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={cpmChartData}
                      layout="vertical"
                      margin={{ top: 4, right: 12, bottom: 4, left: 70 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis
                        type="number"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) => v.toFixed(0)}
                      />
                      <YAxis
                        type="category"
                        dataKey="kol"
                        tick={{ fontSize: 10 }}
                        width={70}
                        interval={0}
                      />
                      <Tooltip
                        cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                        contentStyle={{
                          background: "hsl(var(--background))",
                          border: "1px solid hsl(var(--border))",
                          fontSize: 11,
                        }}
                        formatter={(value, _name, item) => {
                          const p = (item as { payload?: { delivered?: boolean } })?.payload;
                          if (!p?.delivered) return ["未交付", "状态"];
                          if (value == null) return ["无报价或 0 曝光", "CPM"];
                          return [Number(value).toFixed(2), "CPM"];
                        }}
                      />
                      {totals.cpm !== null && (
                        <ReferenceLine
                          x={totals.cpm}
                          stroke="hsl(var(--muted-foreground))"
                          strokeDasharray="4 4"
                          label={{
                            value: `平均 ${totals.cpm.toFixed(1)}`,
                            position: "top",
                            fontSize: 10,
                            fill: "hsl(var(--muted-foreground))",
                          }}
                        />
                      )}
                      <Bar dataKey="cpm" radius={[0, 2, 2, 0]}>
                        {cpmChartData.map((entry, idx) => (
                          <Cell
                            key={idx}
                            fill={
                              !entry.delivered
                                ? "hsl(var(--muted-foreground) / 0.3)"
                                : entry.cpm != null && totals.cpm != null
                                  ? entry.cpm <= totals.cpm
                                    ? "hsl(142 71% 45%)" // green for cheap
                                    : "hsl(0 72% 51%)"   // red for expensive
                                  : "hsl(var(--primary))"
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  低 CPM = 性价比高(绿);高于平均 = 偏贵(红);灰 = 未交付
                </p>
              </div>
            </section>
          )}

          {/* ── Per-video cards ─────────────────────────────── */}
          {confirmed.length > 0 && (
            <section className="report-section-break space-y-3">
              <h2 className="text-lg font-semibold">已交付内容 ({confirmed.length})</h2>
              {confirmed
                .slice()
                .sort((a, b) => {
                  const ax = a.published_at ?? "";
                  const bx = b.published_at ?? "";
                  return ax < bx ? 1 : ax > bx ? -1 : 0;
                })
                .map((d) => {
                  const kol = channels.find((c) => c.channel_id === d.video_channel_id);
                  return (
                    <div
                      key={d.sponsorship_id}
                      className="report-video-card report-card rounded border border-border bg-background p-4 text-sm"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="font-medium">
                          {d.url ? (
                            <a href={d.url} target="_blank" rel="noreferrer" className="hover:underline">
                              {d.title ?? `Video #${d.video_id}`}
                            </a>
                          ) : (
                            d.title ?? `Video #${d.video_id}`
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {d.published_at ? fmtDate(d.published_at) : "—"}
                        </div>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {kol?.channel_name ?? "—"} ·{" "}
                        {platformLabel(kol?.channel_platform ?? "")}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs">
                        <span>
                          曝光 <strong className="tabular-nums">{fmtNumber(d.view_count ?? 0)}</strong>
                        </span>
                        <span>
                          点赞 <strong className="tabular-nums">{fmtNumber(d.like_count ?? 0)}</strong>
                        </span>
                        <span>
                          评论 <strong className="tabular-nums">{fmtNumber(d.comment_count ?? 0)}</strong>
                        </span>
                        {d.archive_drive_url && (
                          <a
                            href={d.archive_drive_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            归档副本 →
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
            </section>
          )}

          {/* ── Notes ──────────────────────────────────────────── */}
          {campaign.notes && (
            <section className="report-card rounded border border-border bg-background p-5">
              <h2 className="mb-2 text-lg font-semibold">备注</h2>
              <div className="whitespace-pre-wrap text-sm text-foreground">
                {campaign.notes}
              </div>
            </section>
          )}

          <footer className="pt-6 text-center text-xs text-muted-foreground">
            Bitbase KOL Management · 投放复盘报告 · {generatedAt}
          </footer>
        </div>
      </div>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
